import { getCurrentScope, onScopeDispose, shallowRef } from "vue";
import { createFinderEmbedTransport } from "#ce/apps/SpFinder/api/finder-embed-transport";
import { createJsonApi } from "#ce/apps/SpFinder/api/json-api";
import { createProviderResultsApi } from "#ce/apps/SpFinder/api/provider-results-api";
import { createFinderHistory } from "#ce/apps/SpFinder/routing/finder-history";
import { cloneFinderData, equalFinderData } from "#ce/components/SpFinder/engine/finder-data";
import { createRequestLifecycle } from "./request-lifecycle";
import { MAP_COMMANDS } from "#ce/components/SpFinder/engine/map-commands";
import { RESULT_COMMANDS } from "#ce/components/SpFinder/engine/result-commands";
import { createNearStorage } from "./near-location";
import { replaceFinderParams } from "#ce/apps/SpFinder/routing/finder-url-codec";

const nearFields = ["zip", "lat", "lng", "distance", "locationDisplayName"];
const descriptionFields = ["visit_type_name", "locationDisplayName"];
const presentationFields = ["view", "result_guid", ...descriptionFields];
const locationCriteria = ["zip", "lat", "lng", "near", "bounds"];

function pickNear(values) {
  return Object.fromEntries(
    nearFields.filter((name) => Object.hasOwn(values, name)).map((name) => [name, values[name]]),
  );
}

function withoutDescriptions(values) {
  return Object.fromEntries(Object.entries(values).filter(([name]) => !descriptionFields.includes(name)));
}

function freeze(value) {
  if (value && typeof value === "object") {
    for (const child of Object.values(value)) {
      freeze(child);
    }

    Object.freeze(value);
  }

  return value;
}

/** Owns immutable activated-search snapshots and abortable subscriptions for one wrapper. */
export function createFinderStore(
  onPublication,
  {
    fetch,
    required = () => true,
    resultsUrl,
    routes,
    commands = {},
    kind,
    scope = {},
    requestContext = {},
    persistLocation = false,
    reloadOnLandingPageSubmit = false,
    navigate,
  } = {},
) {
  const state = shallowRef(
    Object.freeze({
      revision: 0,
      publication: 0,
    }),
  );
  const fixedScope = cloneFinderData(scope);
  const context = cloneFinderData(requestContext);
  const hasFixedLocation = locationCriteria.some(
    (name) => Object.hasOwn(fixedScope, name) || Object.hasOwn(context, name),
  );
  const listeners = new Set();
  let closed = false;
  let fetchingHeld = false;
  let pending;
  let nearStorage;
  let transientNear;
  const history = routes ? createFinderHistory(routes) : undefined;
  const resultsApi = createProviderResultsApi({ fetch, navigate });
  const jsonApi = createJsonApi({ fetch });
  const requestLifecycle = resultsUrl
    ? createRequestLifecycle({
        api: resultsApi,
        required,
        onRequest: reportRequest,
        onComplete: reportCompletion,
        onError: reportError,
      })
    : undefined;
  const embedTransport = createFinderEmbedTransport({ replaceParams: replaceFinderParams });
  const stopHistory = history?.listen(restoreHistory);
  if (history) {
    restore();
  }

  function publish(next) {
    state.value = freeze(next);

    for (const handle of [...listeners]) {
      if (!handle.closed) {
        handle.listener(state.value);
      }
    }
  }

  function subscribe(listener, { signal } = {}) {
    let retired = closed || Boolean(signal?.aborted);

    function disconnect() {
      retired = true;
      listeners.delete(handle);
      signal?.removeEventListener("abort", disconnect);
    }

    const handle = {
      listener,
      disconnect,
      get closed() {
        return retired;
      },
    };

    if (!retired) {
      listeners.add(handle);
      signal?.addEventListener("abort", disconnect, {
        once: true,
      });
      listener(state.value);
    }

    return handle;
  }

  function publishSearch(params, { filterValues } = {}) {
    if (closed) {
      return;
    }

    const previous = state.value;
    const changed = !equalFinderData(previous.params, params);

    publish({
      ...previous,
      params: cloneFinderData(params),
      filterValues: cloneFinderData(filterValues),
      revision: previous.revision + Number(changed),
      publication: previous.publication + 1,
    });
    onPublication?.(state.value);
  }

  function search(params, { route = "results", user = true, replace = false, transientLocation } = {}) {
    if (closed) {
      return;
    }
    queueTransition({ params, route, user, replace, transientLocation });
  }

  function normalizedRoute(route) {
    return state.value.settings?.hasLandingPage === false ? "results" : route;
  }

  /** Mirrors the route the legacy `DoctorFinder` router resolved, without activating another search. */
  function reportRoute(route) {
    if (closed || !["landing", "results"].includes(route)) {
      return;
    }
    route = normalizedRoute(route);
    if (state.value.route !== route) {
      publish({ ...state.value, route });
    }
  }

  function reportSettings(settings) {
    if (closed || equalFinderData(state.value.settings, settings)) {
      return;
    }
    const next = { ...state.value, settings: cloneFinderData(settings) };
    if (next.landingDraft) {
      next.landingDraft = normalizeLandingDraft(next.landingDraft, next.settings);
    }
    if (settings.hasLandingPage === false && next.route) {
      next.route = "results";
    }
    publish(next);
  }

  function normalizeLandingDraft(draft, settings = state.value.settings) {
    const distance = settings?.location?.filterOptions?.distance;
    if (distance?.control !== "slider") {
      return draft;
    }

    const numeric = Number.parseInt(draft.distance ?? distance.default);

    if (distance.max != null) {
      const radius = Number.isFinite(numeric)
        ? Math.min(distance.max, Math.max(distance.min ?? 1, numeric))
        : distance.max;

      return { ...draft, distance: radius };
    }

    if (!distance.values?.length) {
      return draft;
    }

    const radius = Number.isFinite(numeric)
      ? distance.values.reduce((best, stop) => (Math.abs(stop - numeric) < Math.abs(best - numeric) ? stop : best))
      : Math.max(...distance.values);
    return { ...draft, distance: radius };
  }

  function updateLandingDraft(patch, { explicitlySelected = false, transientLocation } = {}) {
    if (closed) {
      return;
    }
    const landingDraft = normalizeLandingDraft({ ...state.value.landingDraft, ...cloneFinderData(patch) });
    const locationChanged = ["zip", "lat", "lng"].some(
      (name) => Object.hasOwn(patch, name) && !equalFinderData(state.value.landingDraft?.[name], patch[name]),
    );
    const landingDraftTransientLocation =
      transientLocation ??
      (explicitlySelected || locationChanged ? false : Boolean(state.value.landingDraftTransientLocation));
    if (
      !equalFinderData(state.value.landingDraft, landingDraft) ||
      state.value.landingDraftTransientLocation !== landingDraftTransientLocation
    ) {
      publish({ ...state.value, landingDraft, landingDraftTransientLocation });
    }
    if (
      nearFields.some((name) => Object.hasOwn(patch, name)) &&
      persistSettings().persistLocation &&
      !hasFixedLocation &&
      !landingDraftTransientLocation &&
      (explicitlySelected || (!landingDraft.zip && landingDraft.lat == null && landingDraft.lng == null))
    ) {
      locationStorage().write(pickNear(landingDraft));
    }
    if (Object.hasOwn(patch, "insurance_plan") && persistSettings().persistInsurancePlan) {
      if (landingDraft.insurance_plan) {
        window.sessionStorage.setItem("insurancePlanFilter", landingDraft.insurance_plan);
      } else {
        window.sessionStorage.removeItem("insurancePlanFilter");
      }
    }
  }

  function navigateFinder(destination) {
    if (closed) {
      return;
    }
    destination = cloneFinderData(destination);
    if (destination.route) {
      destination.route = normalizedRoute(destination.route);
    }
    if (commands.navigate) {
      return commands.navigate(destination);
    }
    search(destination.params ?? {}, { route: destination.route ?? "results" });
  }

  function submitLandingSearch() {
    if (closed) {
      return;
    }
    const draft = cloneFinderData(state.value.landingDraft ?? {});
    const options = { transientLocation: Boolean(state.value.landingDraftTransientLocation) };
    if (commands.submitLandingSearch) {
      return options.transientLocation
        ? commands.submitLandingSearch(draft, options)
        : commands.submitLandingSearch(draft);
    }
    search(draft, options);
  }

  /** Retained values are required criteria and defaults that survive Reset All. */
  function resetAll(retainedParams = {}) {
    search(retainedParams, { transientLocation: false });
  }

  function setPresentation(values) {
    if (closed) {
      return;
    }
    const patch = cloneFinderData(
      Object.fromEntries(Object.entries(values).filter(([name]) => presentationFields.includes(name))),
    );
    if (pending) {
      pending.params = { ...pending.params, ...patch };
      if (pending.normalizedParams) {
        pending.normalizedParams = { ...pending.normalizedParams, ...patch };
      }
      return;
    }
    const previous = state.value;
    const presentation = { ...previous.presentation, ...patch };
    const next = { ...previous, presentation };
    if (previous.request) {
      next.request = { ...previous.request, presentation };
    }
    if (previous.completed && Object.hasOwn(patch, "view")) {
      next.completed = { ...previous.completed, view: patch.view };
    }
    publish(next);
    commitHistory(
      previous.route,
      { ...previous.filterValues, ...previous.requestControls, ...presentation },
      { replace: true },
    );
  }

  function historyValues(values) {
    return { ...withoutDescriptions(values), ...Object.fromEntries(descriptionFields.map((name) => [name, null])) };
  }

  function commitHistory(route, values, options) {
    history?.commit(route, historyValues(values), { ...options, transientLocation: transientNear !== undefined });
  }

  function locationStorage() {
    nearStorage ??= createNearStorage();
    return nearStorage;
  }

  function queueTransition(transition) {
    if (!pending) {
      queueMicrotask(flushTransition);
    }
    const location = Object.fromEntries(
      locationCriteria
        .filter((name) => Object.hasOwn(transition.params, name))
        .map((name) => [name, transition.params[name]]),
    );
    const transientLocation =
      transition.transientLocation ?? (transientNear !== undefined && equalFinderData(location, transientNear));
    transientNear = transientLocation ? cloneFinderData(location) : undefined;
    pending = cloneFinderData({ ...transition, transientLocation });
    requestLifecycle?.cancel();
  }

  function flushTransition() {
    const transition = pending;
    pending = undefined;
    if (closed || !transition) {
      return;
    }

    const {
      params: values,
      route,
      user,
      replace,
      silent,
      persistRestore,
      normalizedParams,
      transientLocation,
    } = transition;
    const filterValues = {};
    const requestControls = {};
    const presentation = {};
    for (const [name, value] of Object.entries(values)) {
      if (["page", "per_page", "sort", "slots_ui_start_date"].includes(name)) {
        requestControls[name] = value;
      } else if (presentationFields.includes(name)) {
        presentation[name] = value;
      } else {
        filterValues[name] = value;
      }
    }
    const params = { ...filterValues, ...fixedScope, ...context };
    const requestParams = { ...params, ...requestControls };
    const previous = state.value;
    const next = {
      ...previous,
      params,
      filterValues,
      requestControls,
      presentation,
      route,
      revision: previous.revision + Number(!equalFinderData(previous.params, params)),
      publication: previous.publication + 1,
    };
    delete next.error;
    if (route !== "results" || fetchingHeld || !required(params)) {
      next.request = { status: route === "results" ? "blocked" : "idle" };
    }
    publish(next);
    onPublication?.(state.value);
    if ((!silent || persistRestore) && persistSettings().persistLocation && !hasFixedLocation && !transientLocation) {
      locationStorage().write(pickNear(values));
    }
    const canRequest = route === "results" && !fetchingHeld && required(params);
    const fullPage =
      previous.route === "landing" && (reloadOnLandingPageSubmit || /^\d{10}$/.test(String(params.query ?? "").trim()));
    if (!silent && canRequest && fullPage && history) {
      const destination = history.destination(route, historyValues({ ...requestParams, ...presentation }));
      resultsApi.navigate(new URL(destination, window.location.href).toString());
      return;
    }
    if (!silent || normalizedParams) {
      commitHistory(route, normalizedParams ?? values, { user, replace: replace || Boolean(normalizedParams) });
    }
    if (canRequest && requestLifecycle) {
      const url = new URL(resultsUrl, window.location.href);
      replaceFinderParams(url.searchParams, requestParams);
      requestLifecycle.request(url.toString(), requestParams, presentation);
    }
  }

  function restoreHistory(snapshot) {
    restore(snapshot, { restoreLocation: false });
  }

  function restore(
    { route, params = {}, transientLocation = false } = history?.current() ?? {},
    { restoreLocation = true } = {},
  ) {
    if (closed || !route) {
      return;
    }
    const persistRestore = restoreLocation && persistSettings().persistLocation && !hasFixedLocation;
    const originalParams = params;
    params = withoutDescriptions(params);
    let normalizedParams;
    if (restoreLocation) {
      const savedLocation = persistRestore ? locationStorage().read() : undefined;
      if (savedLocation) {
        transientLocation = false;
        const otherParams = Object.fromEntries(
          Object.entries(params).filter(([name]) => !nearFields.includes(name) && name !== "near"),
        );
        params = { ...otherParams, ...pickNear(savedLocation) };
      } else if (Object.hasOwn(params, "near")) {
        const { near, ...otherParams } = params;
        params = { ...otherParams, zip: params.zip || near };
      }
      if (!equalFinderData(originalParams, params)) {
        normalizedParams = {
          ...Object.fromEntries(Object.keys(originalParams).map((name) => [name, null])),
          ...params,
        };
      }
    }
    queueTransition({ params, route, silent: true, persistRestore, normalizedParams, transientLocation });
  }

  function reportRequest({ generation, params, url, presentation }) {
    if (closed) {
      return;
    }

    const previous = { ...state.value };
    delete previous.error;
    const request = {
      generation,
      status: "loading",
      params: cloneFinderData(params),
      url,
    };
    if (presentation) {
      request.presentation = cloneFinderData(presentation);
    }
    publish({ ...previous, request });
  }

  function reportCompletion({ generation, resultCount, result, view }) {
    if (closed || state.value.request?.generation !== generation) {
      return;
    }

    const previous = state.value;
    const { params, url, presentation } = previous.request;
    const completedParams = cloneFinderData(params);
    if (presentation?.visit_type_name !== undefined) {
      completedParams.visit_type_name = presentation.visit_type_name;
    }
    publish({
      ...previous,
      request: {
        ...previous.request,
        status: "ready",
      },
      completed: {
        generation,
        params: completedParams,
        url,
        resultCount,
        result,
        view: view ?? presentation?.view,
      },
    });
  }

  function reportView(view) {
    const { completed } = state.value;

    if (closed || !completed) {
      return;
    }

    publish({ ...state.value, completed: { ...completed, view } });
  }

  /** Publishes selection intent, including a repeated selection, without a search transition. */
  function reportMapSelection(resultGUID) {
    if (!closed) {
      publish({
        ...state.value,
        mapSelection: { resultGUID: resultGUID ?? null, revision: (state.value.mapSelection?.revision ?? 0) + 1 },
      });
    }
  }

  function reportMapBoundsMode(mode) {
    if (!closed && ["auto", "manual"].includes(mode) && state.value.mapBoundsMode !== mode) {
      publish({ ...state.value, mapBoundsMode: mode });
    }
  }

  function reportError({ generation, error }) {
    if (closed || state.value.request?.generation !== generation) {
      return;
    }

    const previous = state.value;
    publish({
      ...previous,
      request: {
        ...previous.request,
        status: "error",
      },
      error,
    });
  }

  function reportAttachment(attached) {
    if (!closed) {
      publish({ ...state.value, attached });
    }
  }

  function holdFetching() {
    if (closed) {
      return;
    }
    fetchingHeld = true;
    requestLifecycle?.cancel();
    if (requestLifecycle && state.value.route === "results") {
      const next = { ...state.value, request: { status: "blocked" } };
      delete next.error;
      publish(next);
    }
  }

  function releaseFetching(options) {
    if (closed) {
      return;
    }
    fetchingHeld = false;
    commands.releaseFetching?.(options);
    if (requestLifecycle && state.value.route && !pending) {
      queueTransition({
        route: state.value.route,
        params: { ...state.value.filterValues, ...state.value.requestControls, ...state.value.presentation },
        silent: true,
      });
    }
  }

  function applyRequiredFilters(selection) {
    if (!closed) {
      commands.applyRequiredFilters?.(selection);
    }
  }

  function persistSettings() {
    return commands.persistSettings?.() ?? { persistLocation, persistInsurancePlan: false };
  }

  function resultCommand(name) {
    return function runResultCommand(...args) {
      if (!closed) {
        return commands[name]?.(...args);
      }
    };
  }

  // A request the kind declares reads its endpoint from the finder's settings and its params from the request owner.
  function kindRequest(name, request) {
    return async function runKindRequest(input) {
      const url = request.url(state.value.settings);
      const params = commands.requestParams?.(name, input);
      if (closed || !url || !params) {
        return {};
      }
      const query = new URLSearchParams();
      replaceFinderParams(query, Object.fromEntries(Object.entries(params).sort(([a], [b]) => a.localeCompare(b))));
      return jsonApi.get(query.size ? `${url}?${query}` : url);
    };
  }

  const resultCommands = {
    ...Object.fromEntries(RESULT_COMMANDS.map((name) => [name, resultCommand(name)])),
    ...Object.fromEntries(
      Object.entries(kind?.requests ?? {}).map(([name, request]) => [name, kindRequest(name, request)]),
    ),
  };

  const mapCommands = Object.fromEntries(MAP_COMMANDS.map((name) => [name, resultCommand(name)]));

  function dispose() {
    closed = true;
    pending = undefined;
    stopHistory?.();
    requestLifecycle?.dispose();

    for (const handle of [...listeners]) {
      handle.disconnect();
    }
  }

  if (getCurrentScope()) {
    onScopeDispose(dispose);
  }

  return {
    state,
    resultCommands,
    mapCommands,
    reportMapSelection,
    reportMapBoundsMode,
    dispose,
    subscribe,
    publishSearch,
    reportRoute,
    reportSettings,
    updateLandingDraft,
    submitLandingSearch,
    navigate: navigateFinder,
    search,
    resetAll,
    setPresentation,
    decorateEmbedUrl: embedTransport.decorateUrl,
    restore,
    reportRequest,
    reportCompletion,
    reportError,
    reportView,
    reportAttachment,
    holdFetching,
    releaseFetching,
    applyRequiredFilters,
    persistSettings,
    get fetchingHeld() {
      return fetchingHeld;
    },
    set fetchingHeld(value) {
      if (value) {
        holdFetching();
      } else if (!closed) {
        fetchingHeld = false;
      }
    },
    get closed() {
      return closed;
    },
  };
}
